import type { LearningLanguage, VerbsData } from '../types';
import { normalizeLearningLanguage } from './learningLanguage';

export type VerbPracticePronounRow = [string, string, string, string];

interface VerbPracticeGroupMeta {
  badge: string;
  columnLabel: string;
  pattern: string;
  title: string;
  optionLabel: string;
}

interface VerbPracticeConfig {
  groupOrder: string[];
  groupMeta: Record<string, VerbPracticeGroupMeta>;
  allDeckTitle: string;
  allDeckOptionLabel: string;
  pronounRows: VerbPracticePronounRow[];
  pronounOrder: string[];
}

const VERB_PRACTICE_CONFIG: Record<LearningLanguage, VerbPracticeConfig> = {
  pl: {
    groupOrder: ['1', '2', '3'],
    groupMeta: {
      '1': {
        badge: 'I',
        columnLabel: 'Koniugacja I',
        pattern: '(-ę, -esz)',
        title: 'KONIUGACJA I (‑ę, ‑esz)',
        optionLabel: 'I (‑ę, ‑esz)',
      },
      '2': {
        badge: 'II',
        columnLabel: 'Koniugacja II',
        pattern: '(-ę, -isz/-ysz)',
        title: 'KONIUGACJA II (‑ę, ‑isz/‑ysz)',
        optionLabel: 'II (‑ę, ‑isz/‑ysz)',
      },
      '3': {
        badge: 'III',
        columnLabel: 'Koniugacja III',
        pattern: '(-am, -asz)',
        title: 'KONIUGACJA III (‑am/‑asz)',
        optionLabel: 'III (‑am/‑asz)',
      },
    },
    allDeckTitle: 'KONIUGACJA I + II + III',
    allDeckOptionLabel: 'All (I + II + III)',
    pronounRows: [
      ['ja', 'ja', 'my', 'my'],
      ['ty', 'ty', 'wy', 'wy'],
      ['on', 'on', 'oni', 'oni'],
    ],
    pronounOrder: ['ja', 'ty', 'on', 'my', 'wy', 'oni'],
  },
  lt: {
    groupOrder: ['1', '2', '3', '4'],
    groupMeta: {
      '1': {
        badge: 'A',
        columnLabel: 'Asmenuotė A',
        pattern: '(-a)',
        title: 'ASMENUOTĖ A (‑a)',
        optionLabel: 'A (‑a)',
      },
      '2': {
        badge: 'I',
        columnLabel: 'Asmenuotė I',
        pattern: '(-i)',
        title: 'ASMENUOTĖ I (‑i)',
        optionLabel: 'I (‑i)',
      },
      '3': {
        badge: 'O',
        columnLabel: 'Asmenuotė O',
        pattern: '(-o)',
        title: 'ASMENUOTĖ O (‑o)',
        optionLabel: 'O (‑o)',
      },
      '4': {
        badge: 'IRR',
        columnLabel: 'Netaisyklingi',
        pattern: '',
        title: 'NETAISYKLINGI',
        optionLabel: 'Irregular',
      },
    },
    allDeckTitle: 'ASMENUOTĖS A + I + O + NETAISYKLINGI',
    allDeckOptionLabel: 'All (A + I + O + irregular)',
    pronounRows: [
      ['aš', 'aš', 'mes', 'mes'],
      ['tu', 'tu', 'jūs', 'jūs'],
      ['jis/ji', 'jis/ji', 'jie/jos', 'jie/jos'],
    ],
    pronounOrder: ['aš', 'tu', 'jis/ji', 'mes', 'jūs', 'jie/jos'],
  },
};

export function getVerbPracticeConfig(language?: LearningLanguage | null): VerbPracticeConfig {
  return VERB_PRACTICE_CONFIG[normalizeLearningLanguage(language)];
}

export function getVerbPracticeGroupIds(
  verbsData: VerbsData,
  language?: LearningLanguage | null,
): string[] {
  const config = getVerbPracticeConfig(language);
  const availableGroups = new Set(Object.keys(verbsData));
  const ordered = config.groupOrder.filter((group) => availableGroups.has(group));
  for (const group of Object.keys(verbsData).sort()) {
    if (!ordered.includes(group)) {
      ordered.push(group);
    }
  }
  return ordered.length > 0 ? ordered : [...config.groupOrder];
}

export function getVerbDeckTitle(
  deck: string,
  language?: LearningLanguage | null,
): string {
  const config = getVerbPracticeConfig(language);
  if (deck === 'all') return config.allDeckTitle;
  return config.groupMeta[deck]?.title || deck;
}

export function getVerbDeckOptions(
  verbsData: VerbsData,
  language?: LearningLanguage | null,
): Array<{ value: string; label: string }> {
  const config = getVerbPracticeConfig(language);
  const groups = getVerbPracticeGroupIds(verbsData, language);
  return [
    ...groups.map((group) => ({
      value: group,
      label: config.groupMeta[group]?.optionLabel || group,
    })),
    { value: 'all', label: config.allDeckOptionLabel },
  ];
}

export function getVerbBadgeLabel(
  group: string,
  language?: LearningLanguage | null,
): string {
  const config = getVerbPracticeConfig(language);
  return config.groupMeta[group]?.badge || group;
}

export function buildVerbCardHref(
  group?: string,
  verb?: string,
  language?: LearningLanguage | null,
): string {
  const params = new URLSearchParams();
  if (normalizeLearningLanguage(language) === 'lt') {
    params.set('lang', 'lt');
  }
  if (group) {
    params.set('group', group);
  }
  if (verb) {
    params.set('verb', verb);
  }
  const query = params.toString();
  return query ? `/kon/?${query}` : '/kon/';
}

export function parseVerbCardLanguage(search: string): LearningLanguage | null {
  const params = new URLSearchParams(search);
  return params.get('lang') === 'lt' ? 'lt' : null;
}
